import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Tale, TaleInput, TaleUpdate } from "@/types/tales";
import { convertDoc, uploadImage } from "./utils";

const TALES_COLLECTION = "tales";

// Get all tales
export async function getTales(): Promise<Tale[]> {
  try {
    const talesRef = collection(db, TALES_COLLECTION);
    const q = query(talesRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const tales = querySnapshot.docs.map(doc => {
      const tale = convertDoc<Tale>(doc);
      // Ensure ID is set
      if (!tale.id) {
        console.error("Tale missing ID after conversion:", doc.id);
        tale.id = doc.id;
      }
      return tale;
    });
    
    console.log(`Retrieved ${tales.length} tales from Firestore`);
    return tales;
  } catch (error) {
    console.error("Error fetching tales:", error);
    throw new Error("Failed to fetch tales");
  }
}

// Get a tale by ID
export async function getTale(id: string): Promise<Tale | null> {
  try {
    if (!id) {
      console.error("Attempted to fetch tale with empty ID");
      return null;
    }
    
    const taleRef = doc(db, TALES_COLLECTION, id);
    const docSnapshot = await getDoc(taleRef);
    
    if (!docSnapshot.exists()) {
      console.log(`Tale with ID ${id} not found`);
      return null;
    }
    
    const tale = convertDoc<Tale>(docSnapshot);
    // Ensure ID is set
    if (!tale.id) {
      console.error("Tale missing ID after conversion:", id);
      tale.id = id;
    }
    
    return tale;
  } catch (error) {
    console.error(`Error fetching tale with ID ${id}:`, error);
    throw new Error("Failed to fetch tale");
  }
}

// Add a new tale
export async function addTale(taleData: TaleInput): Promise<Tale> {
  try {
    const talesRef = collection(db, TALES_COLLECTION);
    const docRef = await addDoc(talesRef, {
      ...taleData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return {
      id: docRef.id,
      ...taleData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  } catch (error) {
    console.error("Error adding tale:", error);
    throw new Error("Failed to add tale");
  }
}

// Update a tale
export async function updateTale(id: string, taleData: TaleUpdate): Promise<Tale> {
  try {
    const taleRef = doc(db, TALES_COLLECTION, id);
    await updateDoc(taleRef, {
      ...taleData,
      updatedAt: serverTimestamp(),
    });
    
    const updatedTale = await getTale(id);
    if (!updatedTale) {
      throw new Error("Failed to retrieve updated tale");
    }
    
    return updatedTale;
  } catch (error) {
    console.error(`Error updating tale with ID ${id}:`, error);
    throw new Error("Failed to update tale");
  }
}

// Delete a tale
export async function deleteTale(taleId: string): Promise<void> {
  try {
    const taleRef = doc(db, TALES_COLLECTION, taleId);
    
    // Check if tale exists
    const taleDoc = await getDoc(taleRef);
    if (!taleDoc.exists()) {
      throw new Error("Tale not found");
    }
    
    // Delete the tale
    await deleteDoc(taleRef);
  } catch (error) {
    console.error(`Error deleting tale with ID ${taleId}:`, error);
    throw new Error("Failed to delete tale");
  }
}

// Upload a tale image
export async function uploadTaleImage(file: File): Promise<string> {
  const path = `tales/${Date.now()}_${file.name}`;
  return uploadImage(file, path);
}

// Get tales by category
export async function getTalesByCategory(category: string): Promise<Tale[]> {
  try {
    const talesRef = collection(db, TALES_COLLECTION);
    const q = query(
      talesRef,
      where("category", "==", category),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => convertDoc<Tale>(doc));
  } catch (error) {
    console.error(`Error fetching tales for category ${category}:`, error);
    throw new Error("Failed to fetch tales by category");
  }
}
